import classNames from "classnames";
import { Csv } from "components/icons/Csv";
import { Doc } from "components/icons/Doc";
import { FallbackFileIcon } from "components/icons/FallbackFileIcon";
import { FolderSolid } from "components/icons/FolderSolid";
import { Jpg } from "components/icons/Jpg";
import { LockedFolder } from "components/icons/LockedFolder";
import { MultiFiles } from "components/icons/MultiFiles";
import { Pdf } from "components/icons/Pdf";
import { Png } from "components/icons/Png";
import { PrivateFolder } from "components/icons/PrivateFolder";
import { Xls } from "components/icons/Xls";
import * as FILE_TYPE from "constants/fileTypes";

type FileIconProps = {
  fileType?: keyof typeof FILE_TYPE | (string & {});
  size?: string;
  variant?: "light" | "dark";
} & React.SVGProps<SVGSVGElement>;

const FILE_TYPE_MAP = {
  [FILE_TYPE.PDF]: [FILE_TYPE.PDF, FILE_TYPE.APPLICATION_PDF],
  [FILE_TYPE.IMAGE]: [
    FILE_TYPE.IMAGE,
    FILE_TYPE.SVG,
    FILE_TYPE.JPG,
    FILE_TYPE.JPEG,
    FILE_TYPE.GIF,
    FILE_TYPE.IMAGE_JPEG,
    FILE_TYPE.IMAGE_GIF,
    FILE_TYPE.IMAGE_BMP,
    FILE_TYPE.IMAGE_SVG_XML,
  ],
  [FILE_TYPE.PNG]: [FILE_TYPE.PNG, FILE_TYPE.IMAGE_PNG],
  [FILE_TYPE.CSV]: [FILE_TYPE.CSV, FILE_TYPE.TEXT_CSV],
  [FILE_TYPE.DOC]: [
    FILE_TYPE.DOC,
    FILE_TYPE.DOCX,
    FILE_TYPE.TXT,
    FILE_TYPE.PPT,
    FILE_TYPE.PPTX,
    FILE_TYPE.APPLICATION_VND_MS_POWERPOINT,
    FILE_TYPE.APPLICATION_VND_PRESENTATION,
    FILE_TYPE.APPLICATION_VND_DOCUMENT,
    FILE_TYPE.APPLICATION_MSWORD,
  ],
  [FILE_TYPE.EXCEL]: [
    FILE_TYPE.EXCEL,
    FILE_TYPE.XLSX,
    FILE_TYPE.XLSM,
    FILE_TYPE.XLSB,
    FILE_TYPE.XML,
    FILE_TYPE.XLR,
    FILE_TYPE.XLA,
    FILE_TYPE.XLS,
    FILE_TYPE.XLSX1,
    FILE_TYPE.XLS1,
    FILE_TYPE.APPLICATION_XML,
    FILE_TYPE.APPLICATION_VND_MS_EXCEL,
  ],
  [FILE_TYPE.FOLDER]: [FILE_TYPE.FOLDER],
  [FILE_TYPE.MULTI_FILES]: [FILE_TYPE.MULTI_FILES],
  [FILE_TYPE.LOCKED_FOLDER]: [FILE_TYPE.LOCKED_FOLDER],
  [FILE_TYPE.PRIVATE_FOLDER]: [FILE_TYPE.PRIVATE_FOLDER],
};

export const FileIcon = ({
  fileType,
  variant = "dark",
  ...props
}: FileIconProps) => {
  const getFileType = (fileType?: string): string | null => {
    if (!fileType) return null;

    return (
      Object.keys(FILE_TYPE_MAP).find((key) =>
        FILE_TYPE_MAP[key as keyof typeof FILE_TYPE_MAP].includes(fileType)
      ) || null
    );
  };
  switch (getFileType(fileType)) {
    case FILE_TYPE.PDF:
      return (
        <Pdf
          {...props}
          className={classNames({
            "t-text-text-60": variant === "dark",
            "t-text-surface": variant === "light",
          })}
        />
      );

    case FILE_TYPE.IMAGE:
      return (
        <Jpg
          {...props}
          className={classNames({
            "t-text-text-60": variant === "dark",
            "t-text-surface": variant === "light",
          })}
        />
      );

    case FILE_TYPE.PNG:
      return (
        <Png
          {...props}
          className={classNames({
            "t-text-text-60": variant === "dark",
            "t-text-surface": variant === "light",
          })}
        />
      );

    case FILE_TYPE.CSV:
      return (
        <Csv
          {...props}
          className={classNames({
            "t-text-text-60": variant === "dark",
            "t-text-surface": variant === "light",
          })}
        />
      );

    case FILE_TYPE.DOC:
      return (
        <Doc
          {...props}
          className={classNames({
            "t-text-text-60": variant === "dark",
            "t-text-surface": variant === "light",
          })}
        />
      );

    case FILE_TYPE.EXCEL:
      return (
        <Xls
          {...props}
          className={classNames({
            "t-text-text-60": variant === "dark",
            "t-text-surface": variant === "light",
          })}
        />
      );

    case FILE_TYPE.FOLDER:
      return <FolderSolid {...props} />;

    case FILE_TYPE.LOCKED_FOLDER:
      return <LockedFolder {...props} />;

    case FILE_TYPE.PRIVATE_FOLDER:
      return <PrivateFolder {...props} />;

    case FILE_TYPE.MULTI_FILES:
      return <MultiFiles {...props} className="t-text-purple-100" />;

    default:
      return <FallbackFileIcon {...props} />;
  }
};
